import { z } from 'zod';

/**
 * Entity-agnostic primitives for AI-extracted form prefill.
 *
 * These were lifted out of `complaint-extraction.ts` so both the complaint
 * and incident (and any future) extraction stacks share one definition of:
 *  - the per-field `extractedField` wrapper (loose LLM shape + strict
 *    persisted shape) and its `{value, confidence, sourceField, sourceQuote}`
 *    contract,
 *  - the enum-matching helpers (`matchAllowed`, `passthrough`, `mapEnumField`),
 *  - the prefill state machine vocabulary (`PREFILL_FIELD_STATE`),
 *  - the client-submitted audit-input schema (`PrefillAuditInputSchema`) and
 *    the persisted per-field audit entry (`prefillFieldEntrySchema`).
 *
 * Entity-specific schemas (the section tree, the loose→strict translation,
 * the persisted record schema) live in the per-entity files and compose these.
 *
 * Browser-safe by construction — no `@server/*` imports.
 */

// ──────────────────────────────────────────────────────────────────────────
// Confidence + source enums
// ──────────────────────────────────────────────────────────────────────────

export const EXTRACTION_CONFIDENCE = ['HIGH', 'MEDIUM', 'LOW'] as const;
export type ExtractionConfidence = (typeof EXTRACTION_CONFIDENCE)[number];

export const EXTRACTION_SOURCE_FIELD = ['subject', 'bodyText', 'sender'] as const;
export type ExtractionSourceField = (typeof EXTRACTION_SOURCE_FIELD)[number];

// ──────────────────────────────────────────────────────────────────────────
// Per-field "extractedField" wrapper helpers
// ──────────────────────────────────────────────────────────────────────────

/**
 * Longest accepted `sourceQuote`. Generous on purpose: for narrative-style
 * fields the model tends to quote the entire pasted body, and OpenAI calls run
 * with `strictJsonSchema: false`, so this bound is enforced only by the Zod
 * parse — a failing quote must never cost us the extracted value (see the
 * per-property `.catch` below). Display/audit surfaces truncate separately.
 */
export const MAX_SOURCE_QUOTE_CHARS = 5000;

/**
 * Per-field LLM extraction payload. Kept deliberately simple — no unions
 * or `.transform()`s — so the constraint-based JSON-schema decoders used by
 * Gemini/OpenAI structured output don't blow up on state-space explosion.
 * Lenience comes from `.catch(undefined)` per field plus per-section optionality.
 * `sourceQuote` carries its own `.catch(undefined)` so an over-long or invalid
 * quote drops only the attribution — never the whole field (the outer catch
 * used to swallow the entire narrative when the model quoted a >500-char body).
 */
export function llmExtractedField<TValueSchema extends z.ZodTypeAny>(valueSchema: TValueSchema) {
    return z
        .object({
            value: valueSchema,
            confidence: z.enum(EXTRACTION_CONFIDENCE).optional(),
            sourceField: z.enum(EXTRACTION_SOURCE_FIELD).optional(),
            sourceQuote: z.string().max(MAX_SOURCE_QUOTE_CHARS).optional().catch(undefined),
        })
        .optional()
        .catch(undefined);
}

export function llmSection<TShape extends z.ZodRawShape>(shape: TShape) {
    return z.object(shape).optional().catch(undefined);
}

/**
 * Strict per-field payload (what we persist). `value` is narrowed to the
 * destination type; metadata is preserved.
 */
export function strictExtractedField<TValueSchema extends z.ZodTypeAny>(valueSchema: TValueSchema) {
    return z.object({
        value: valueSchema,
        confidence: z.enum(EXTRACTION_CONFIDENCE).optional(),
        sourceField: z.enum(EXTRACTION_SOURCE_FIELD).optional(),
        sourceQuote: z.string().max(MAX_SOURCE_QUOTE_CHARS).optional(),
    });
}

export type ExtractedField<TValue> = {
    value: TValue;
    confidence?: ExtractionConfidence;
    sourceField?: ExtractionSourceField;
    sourceQuote?: string;
};

// ──────────────────────────────────────────────────────────────────────────
// Enum-matching helpers used by per-entity loose→strict translations
// ──────────────────────────────────────────────────────────────────────────

export function matchAllowed(value: string | undefined, allowed: readonly string[]): string | undefined {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    // Case-insensitive match, then return the canonical casing from the allow list.
    const lower = trimmed.toLowerCase();
    return allowed.find((option) => option.toLowerCase() === lower);
}

export function passthrough<TValue>(field: ExtractedField<TValue> | undefined): ExtractedField<TValue> | undefined {
    if (!field) return undefined;
    return { ...field };
}

export function mapEnumField(
    field: ExtractedField<string> | undefined,
    allowed: readonly string[],
): ExtractedField<string> | undefined {
    if (!field) return undefined;
    const match = matchAllowed(field.value, allowed);
    if (!match) return undefined;
    return { ...field, value: match };
}

/**
 * Resolve LLM-supplied product references to the numeric ASIC codes
 * `asicProductsSchema` expects (`[44]`). Robust to the model returning the bare
 * code ("44"), a leading-code label ("44 - Motor vehicle—Comprehensive"), or the
 * exact label ("Motor vehicle—Comprehensive") — anything that doesn't resolve to
 * an allowed code is dropped (so a hallucinated row-ordinal like "1" is rejected
 * rather than silently mis-mapped). Shared by the complaint + incident
 * `claim.productCodes` translations.
 */
export function mapProductCodesField(
    field: ExtractedField<string[]> | undefined,
    allowed: readonly { code: string; label: string }[],
): ExtractedField<number[]> | undefined {
    if (!field) return undefined;
    const validCodes = new Set(allowed.map((p) => p.code));
    const codeByLabel = new Map(allowed.map((p) => [p.label.trim().toLowerCase(), p.code]));
    const seen = new Set<number>();
    const matched: number[] = [];
    for (const raw of field.value) {
        const v = typeof raw === 'string' ? raw.trim() : String(raw);
        if (!v) continue;
        // Prefer a leading integer ("44" or "44 - Motor vehicle—Comprehensive");
        // fall back to an exact label match if the model echoed the label only.
        const lead = v.match(/^(\d+)/)?.[1];
        const code = lead && validCodes.has(lead) ? lead : codeByLabel.get(v.toLowerCase());
        if (!code) continue;
        const n = Number.parseInt(code, 10);
        if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
        seen.add(n);
        matched.push(n);
    }
    if (matched.length === 0) return undefined;
    // Cap at MAX_ASIC_PRODUCT_SLOTS (3) — matches the server schema cap.
    return { ...field, value: matched.slice(0, 3) };
}

/**
 * Validate an LLM-supplied date string: drop it if unparseable or after the
 * supplied upper bound. Keeps the original string value when valid. Shared by
 * the complaint (`dtReceived`) and incident (`dateOccurred`, `dtIdentified`)
 * date translations.
 */
export function normaliseDateWithinBound(
    field: ExtractedField<string> | undefined,
    maxIso: string | undefined,
): ExtractedField<string> | undefined {
    if (!field) return undefined;
    const raw = typeof field.value === 'string' ? field.value.trim() : '';
    if (!raw) return undefined;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return undefined;
    if (maxIso) {
        const max = new Date(maxIso);
        if (!Number.isNaN(max.getTime()) && parsed.getTime() > max.getTime()) return undefined;
    }
    return { ...field, value: raw };
}

// ──────────────────────────────────────────────────────────────────────────
// Prefill state machine + audit schemas (entity-agnostic)
//
// The client submits ONLY the per-field state map; the server fills in every
// other audit attribute (correlation ID, confidence, source quote, value
// hash) from the persisted extraction record. So clients cannot lie about
// provenance.
// ──────────────────────────────────────────────────────────────────────────

export const PREFILL_AUDIT_SCHEMA_VERSION = 1;

export const PREFILL_FIELD_STATE = ['ai_only', 'ai_accepted', 'ai_modified', 'ai_rejected', 'manual'] as const;
export type PrefillFieldState = (typeof PREFILL_FIELD_STATE)[number];

const prefillFieldInputEntrySchema = z
    .object({
        state: z.enum(PREFILL_FIELD_STATE),
    })
    .strict();

/**
 * Client-submitted audit input. Only the per-field state map; everything else
 * is server-built from the source extraction record.
 */
export const PrefillAuditInputSchema = z
    .object({
        fields: z.record(z.string().min(1).max(80), prefillFieldInputEntrySchema),
    })
    .strict();

export type PrefillAuditInput = z.infer<typeof PrefillAuditInputSchema>;

/**
 * Persisted per-field audit entry (lives on the destination row's
 * `prefillAudit.fields[path]`). Used to compose each entity's persisted audit
 * record schema.
 */
export const prefillFieldEntrySchema = z
    .object({
        state: z.enum(PREFILL_FIELD_STATE),
        confidence: z.enum(EXTRACTION_CONFIDENCE).optional(),
        sourceField: z.enum(EXTRACTION_SOURCE_FIELD).optional(),
        sourceQuoteExcerpt: z.string().max(200).optional(),
        suggestedValueHash: z.string().max(64).optional(),
    })
    .strict();
